/**
 * Rate formatting helpers.
 *
 * <p>Commission rates are stored as exact fractions (e.g. 0.013475) and are applied to money at
 * full precision. When a rate is shown to a human it is rounded **down** to two decimal places, so
 * a publisher can never advertise a percentage higher than the one they are actually paid. Rounding
 * to nearest would let 1.3475% display as 1.35%, which overstates the rate.
 */

/**
 * Floors a number to a fixed number of decimal places.
 *
 * <p>Scaling is done through a fixed-precision string first. Multiplying by a power of ten in
 * binary floating point can land just below the true decimal — `1.29 * 100` is
 * `128.99999999999997` — and a naive `Math.floor` would then return 1.28 instead of 1.29.
 */
export function floorTo(value, decimals = 2) {
  const factor = 10 ** decimals
  const scaled = Number((value * factor).toFixed(6))
  return Math.floor(scaled) / factor
}

/**
 * Formats a value that is already a percentage (e.g. 1.3475) as a floored percentage string.
 *
 * @param {number|string|null|undefined} percent the percentage, not a fraction
 * @param {number} [decimals] decimal places to keep
 * @returns {string} e.g. "1.34%", or an em dash when the value is missing
 */
export function formatPercent(percent, decimals = 2) {
  if (percent == null || percent === '') return '—'
  const value = Number(percent)
  if (Number.isNaN(value)) return '—'
  return `${floorTo(value, decimals).toFixed(decimals)}%`
}

/**
 * Formats a rate fraction (e.g. 0.013475) as a floored percentage string.
 *
 * @param {number|string|null|undefined} fraction the rate as a fraction of 1
 * @param {number} [decimals] decimal places to keep
 * @returns {string} e.g. "1.34%", or an em dash when the value is missing
 */
export function formatRatePercent(fraction, decimals = 2) {
  if (fraction == null || fraction === '') return '—'
  const value = Number(fraction)
  if (Number.isNaN(value)) return '—'
  return formatPercent(value * 100, decimals)
}
